import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api/api";

const WithdrawPage = () => {
  const navigate = useNavigate();

  const [amount, setAmount] = useState("");
  const [bankCode, setBankCode] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [banks, setBanks] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [withdrawalHistory, setWithdrawalHistory] = useState([]);
  const [orders, setOrders] = useState([]);
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info");
  const [showOrderDropdown, setShowOrderDropdown] = useState(false);
  const [isManualAmount, setIsManualAmount] = useState(false);

  // Load all necessary data
  useEffect(() => {
    const loadData = async () => {
      try {
        const [bankRes, walletRes, orderRes, historyRes] = await Promise.all([
          api.bank.list(),
          api.withdrawal.balance(),
          api.orders.vendorMine(),
          api.withdrawal.listMine(),
        ]);

        if (bankRes?.status) setBanks(bankRes.banks || []);
        if (walletRes?.status) setWallet(walletRes.wallet);

        const history = historyRes?.status ? historyRes.withdrawals || [] : [];
        setWithdrawalHistory(history);

        if (orderRes?.orders?.length) {
          const alreadyWithdrawnOrderIds = history
            .flatMap((w) => w.orderIds || [w.orderId])
            .filter(Boolean);

          const eligible = orderRes.orders.filter((order) => {
            return (
              order.paymentStatus === "paid" &&
              order.confirmed === true &&
              ["confirmed", "delivered", "picked-up"].includes(order.status) &&
              !alreadyWithdrawnOrderIds.includes(order.orderId) &&
              !alreadyWithdrawnOrderIds.includes(order._id)
            );
          });

          setOrders(eligible);
        }
      } catch (err) {
        console.error("Error loading data:", err);
      }
    };

    loadData();
  }, []);

  // Calculate total earnings from selected orders
  const calculateVendorEarnings = (order) => {
    if (!order?.items?.length) return 0;
    return order.items.reduce((sum, item) => {
      const price = Number(item.basePrice ?? item.pricePerUnit ?? 0);
      const qty = Number(item.quantity ?? 0);
      return sum + price * qty;
    }, 0);
  };

  const totalSelectedAmount = selectedOrderIds.reduce((sum, id) => {
    const order = orders.find((o) => o._id === id || o.orderId === id);
    return sum + (order ? calculateVendorEarnings(order) : 0);
  }, 0);

  // Auto-fill amount when selection changes (only if not in manual mode)
  useEffect(() => {
    if (!isManualAmount && totalSelectedAmount > 0) {
      setAmount(totalSelectedAmount.toFixed(0));
    }
  }, [totalSelectedAmount, isManualAmount]);

  // Reset manual mode when no orders are selected
  useEffect(() => {
    if (selectedOrderIds.length === 0) {
      setIsManualAmount(false);
      setAmount("");
    }
  }, [selectedOrderIds.length]);

  const toggleOrderSelection = (orderId) => {
    setSelectedOrderIds((prev) =>
      prev.includes(orderId)
        ? prev.filter((id) => id !== orderId)
        : [...prev, orderId]
    );
  };

  const selectAllOrders = () => {
    if (selectedOrderIds.length === orders.length) {
      setSelectedOrderIds([]);
    } else {
      setSelectedOrderIds(orders.map((o) => o._id));
    }
  };

  const handleAmountChange = (e) => {
    const value = e.target.value.replace(/\D/g, ""); // Only digits

    if (value === "") {
      setAmount("");
      return;
    }

    const numValue = Number(value);

    // Prevent exceeding total selected amount
    if (numValue > totalSelectedAmount) {
      setAmount(totalSelectedAmount.toFixed(0));
    } else {
      setAmount(value);
    }

    setIsManualAmount(true); // User is now manually editing
  };

  const handleWithdraw = async () => {
    if (selectedOrderIds.length === 0) {
      setMessage("Please select at least one order");
      setMessageType("error");
      return;
    }

    if (!bankCode) {
      setMessage("Please select a bank");
      setMessageType("error");
      return;
    }

    if (!accountNumber || accountNumber.length !== 10) {
      setMessage("Please enter a valid 10-digit account number");
      setMessageType("error");
      return;
    }

    const withdrawAmount = Number(amount);
    if (!withdrawAmount || withdrawAmount > totalSelectedAmount) {
      setMessage("Invalid withdrawal amount");
      setMessageType("error");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const res = await api.withdrawal.create({
        amount: withdrawAmount,
        bankCode,
        accountNumber,
        orderIds: selectedOrderIds,
      });

      if (res.status) {
        setMessage("Withdrawal request submitted successfully!");
        setMessageType("success");

        // Reset form
        setSelectedOrderIds([]);
        setBankCode("");
        setAccountNumber("");
        setAmount("");
        setIsManualAmount(false);

        // Refresh data
        window.location.reload();
      } else {
        setMessage(res.message || "Withdrawal failed");
        setMessageType("error");
      }
    } catch (err) {
      console.error(err);
      setMessage(err?.data?.message || "Something went wrong. Please try again.");
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  const selectedCount = selectedOrderIds.length;

  return (
    <div className="min-h-screen bg-[#fff8e1] pb-16">
      <div className="max-w-2xl mx-auto px-5 pt-16">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => navigate(-1)}
            className="text-[#5d4037] hover:text-[#8d6e63] text-2xl font-semibold"
          >
            ← Back
          </button>
          <h1 className="text-3xl font-bold text-[#5d4037]">Withdraw Earnings</h1>
        </div>

        {/* Balance Card */}
        <div className="bg-white rounded-2xl shadow-md p-6 mb-8 border border-[#e0d7c6]">
          <p className="text-sm text-gray-600 mb-1">Available Balance</p>
          <p className="text-4xl font-bold text-[#5d4037]">
            ₦{wallet ? Number(wallet.balance / 100).toLocaleString() : "0.00"}
          </p>
        </div>

        {/* Main Form */}
        <div className="bg-white rounded-2xl shadow-md p-6 mb-10 border border-[#e0d7c6]">
          {/* Order Selection */}
          <div className="mb-6">
            <label className="block text-[#5d4037] font-semibold mb-2">
              Select Orders to Withdraw From
            </label>

            <div className="relative">
              <button
                type="button"
                onClick={() => setShowOrderDropdown(!showOrderDropdown)}
                className="w-full p-4 text-left border border-[#5d4037]/40 rounded-xl bg-gray-50 hover:bg-gray-100 flex justify-between items-center"
              >
                <span>
                  {selectedCount === 0
                    ? "Choose delivered/confirmed orders..."
                    : `${selectedCount} order${selectedCount > 1 ? "s" : ""} selected`}
                </span>
                <span className="text-gray-500">▼</span>
              </button>

              {showOrderDropdown && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-[#5d4037]/30 rounded-xl shadow-xl max-h-80 overflow-y-auto">
                  {orders.length === 0 ? (
                    <div className="p-6 text-center text-gray-500">
                      No eligible orders available for withdrawal
                    </div>
                  ) : (
                    <>
                      <div className="p-3 border-b sticky top-0 bg-white flex justify-between items-center">
                        <button
                          onClick={selectAllOrders}
                          className="text-sm text-[#c2452d] font-medium hover:underline"
                        >
                          {selectedCount === orders.length ? "Deselect All" : "Select All"}
                        </button>
                        <span className="text-sm text-gray-500">
                          {selectedCount} / {orders.length}
                        </span>
                      </div>

                      {orders.map((order) => {
                        const earnings = calculateVendorEarnings(order);
                        const isSelected = selectedOrderIds.includes(order._id);

                        return (
                          <label
                            key={order._id}
                            className="flex items-center px-4 py-4 hover:bg-gray-50 cursor-pointer border-b last:border-none"
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleOrderSelection(order._id)}
                              className="w-5 h-5 text-[#c2452d] rounded focus:ring-[#c2452d]"
                            />
                            <div className="ml-4 flex-1">
                              <p className="font-medium text-[#5d4037]">
                                Order #{order.orderId}
                              </p>
                              <p className="text-sm text-gray-600">
                                ₦{earnings.toLocaleString()} •{" "}
                                {new Date(order.createdAt).toLocaleDateString()}
                              </p>
                            </div>
                          </label>
                        );
                      })}
                    </>
                  )}
                </div>
              )}
            </div>

            {selectedCount > 0 && (
              <p className="mt-3 text-sm text-gray-600">
                Total Available: <strong>₦{totalSelectedAmount.toLocaleString()}</strong>
              </p>
            )}
          </div>

          {/* Amount Input */}
          <div className="mb-6">
            <label className="block text-[#5d4037] font-semibold mb-2">
              Amount to Withdraw (₦)
            </label>
            <div className="relative">
              <input
                type="text"
                value={amount}
                onChange={handleAmountChange}
                placeholder="Enter amount"
                className="w-full p-4 border border-[#5d4037]/40 rounded-xl bg-gray-50 text-lg font-semibold text-[#5d4037] focus:outline-none focus:ring-2 focus:ring-[#c2452d]"
              />
              {isManualAmount && amount && (
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-amber-600 font-medium">
                  Manual
                </span>
              )}
            </div>
            {totalSelectedAmount > 0 && (
              <p className="mt-1.5 text-xs text-gray-500">
                Maximum possible: ₦{totalSelectedAmount.toLocaleString()}
              </p>
            )}
          </div>

          {/* Bank Selection */}
          <div className="mb-6">
            <label className="block text-[#5d4037] font-semibold mb-2">Bank</label>
            <select
              value={bankCode}
              onChange={(e) => setBankCode(e.target.value)}
              className="w-full p-4 border border-[#5d4037]/40 rounded-xl bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#c2452d]"
            >
              <option value="">Select Bank</option>
              {banks.map((bank) => (
                <option key={bank.code} value={bank.code}>
                  {bank.name}
                </option>
              ))}
            </select>
          </div>

          {/* Account Number */}
          <div className="mb-8">
            <label className="block text-[#5d4037] font-semibold mb-2">Account Number</label>
            <input
              type="text"
              maxLength={10}
              value={accountNumber}
              onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, ""))}
              placeholder="Enter 10-digit account number"
              className="w-full p-4 border border-[#5d4037]/40 rounded-xl bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#c2452d]"
            />
          </div>

          {/* Submit Button */}
          <button
            onClick={handleWithdraw}
            disabled={
              loading ||
              selectedCount === 0 ||
              !bankCode ||
              accountNumber.length !== 10 ||
              !amount ||
              Number(amount) === 0
            }
            className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${
              loading || selectedCount === 0 || !amount
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-[#c2452d] hover:bg-[#a63924] text-white shadow-md"
            }`}
          >
            {loading ? "Processing Withdrawal..." : "Confirm Withdrawal"}
          </button>

          {message && (
            <div
              className={`mt-6 p-4 rounded-xl text-center font-medium ${
                messageType === "success"
                  ? "bg-green-100 text-green-800"
                  : "bg-red-100 text-red-800"
              }`}
            >
              {message}
            </div>
          )}
        </div>

        {/* Withdrawal History */}
        {withdrawalHistory.length > 0 && (
          <div className="bg-white rounded-2xl shadow-md p-6 border border-[#e0d7c6]">
            <h2 className="text-2xl font-bold text-[#5d4037] mb-5">Withdrawal History</h2>
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {withdrawalHistory.map((w) => (
                <div
                  key={w._id}
                  className="p-4 bg-gray-50 rounded-xl border flex flex-col sm:flex-row sm:justify-between gap-3"
                >
                  <div>
                    <p className="font-bold text-xl text-[#5d4037]">
                      ₦{Number(w.amount).toLocaleString()}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      {w.orderIds?.length ? `${w.orderIds.length} orders` : "1 order"}
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(w.createdAt).toLocaleString()}
                    </p>
                  </div>
                  <span
                    className={`px-5 py-1.5 rounded-full text-sm font-semibold self-start ${
                      w.status === "success"
                        ? "bg-green-100 text-green-800"
                        : w.status === "pending"
                        ? "bg-yellow-100 text-yellow-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {w.status.toUpperCase()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WithdrawPage;